/**
 * Which per-message actions the Conversation API will accept.
 *
 * There is no edit or delete time window in the product: `ConversationMessageDto`
 * carries only `sentAt` / `editedAt`, and `UpdateMessage` / `DeleteMessage` take
 * no deadline or capability flag, so nothing here expires a message. The rules
 * below are the ones the API already imposes — the sender must own the message,
 * the row must exist server-side to be addressable by id, and an edit must have
 * a text body to replace. The backend stays the source of truth; these
 * predicates only keep the UI from offering an action that is certain to fail.
 *
 * If the backend ever publishes a window, express it here from the server value
 * (`createdAt` mirrors the server `sentAt`) so both platforms inherit one rule.
 */
import { isLocalMessageId } from '../api/conversation/conversationMapper';
import type { ExtendedChatMessage } from '../types/chat';

/** The fields an action decision needs; accepts any richer message object. */
export type ChatMessageActionSubject = Pick<
  ExtendedChatMessage,
  '_id' | 'text' | 'user' | 'pending' | 'failed' | 'system'
>;

const hasIdentity = (value: string | number | null | undefined): boolean =>
  value != null && String(value).trim() !== '';

/**
 * True when the message was sent by the given user. An unidentified sender or
 * viewer is never a match, so a row the payload left blank is not mistaken for
 * the reader's own message.
 */
export const isChatMessageSentByUser = (
  message: Pick<ChatMessageActionSubject, 'user'> | null | undefined,
  currentUserId: string | number | null | undefined,
): boolean => {
  const senderId = message?.user?._id;
  if (!hasIdentity(senderId) || !hasIdentity(currentUserId)) return false;
  return String(senderId) === String(currentUserId);
};

/**
 * True once the backend has confirmed the row, which is what makes it
 * addressable by message id. Rows still in flight, rows whose send failed, and
 * rows the response returned without an id all carry a local placeholder id
 * that `UpdateMessage` / `DeleteMessage` cannot resolve.
 */
export const isChatMessagePersisted = (
  message: ChatMessageActionSubject | null | undefined,
): boolean => {
  if (!message) return false;
  if (message.pending === true || message.failed === true) return false;
  if (isLocalMessageId(message._id)) return false;
  return hasIdentity(message._id);
};

/**
 * True when the reader may edit this message. `UpdateMessage` replaces
 * `bodyText` only, so an attachment-only message has nothing editable and its
 * Edit action would dead-end on an empty body.
 */
export const canEditChatMessage = (
  message: ChatMessageActionSubject | null | undefined,
  currentUserId: string | number | null | undefined,
): boolean => {
  if (!message || message.system === true) return false;
  if (!isChatMessageSentByUser(message, currentUserId)) return false;
  if (!isChatMessagePersisted(message)) return false;
  return (message.text ?? '').trim() !== '';
};

/** True when the reader may delete this message. */
export const canDeleteChatMessage = (
  message: ChatMessageActionSubject | null | undefined,
  currentUserId: string | number | null | undefined,
): boolean => {
  if (!message || message.system === true) return false;
  if (!isChatMessageSentByUser(message, currentUserId)) return false;
  return isChatMessagePersisted(message);
};
